import type {
  Alert,
  Phone,
} from "@tandemdiabetes/sugarmate-api-react-client"
import {
  LargeMenuModalItem,
  LargeMenuModalItemButton,
  LargeMenuModalItemIcon,
  LargeMenuModalItemText,
  LargeMenuModalItemLabel,
  LargeMenuModalItemSubtext,
  LargeMenuModalItemIconButtons,
  IconButton,
  PhoneIcon,
  TrashCanOutlineIcon,
  EllipsisIcon,
  EditIcon,
  Popover,
  PopoverTrigger,
  PopoverMenu,
  PopoverMenuItem,
  PopoverMenuItemIcon,
  PopoverMenuItemSeparator,
  useToasterContext,
} from "@tandemdiabetes/sugarmate-react-components"
import {
  useCallback,
  useMemo,
  useState,
} from "react"
import styles from "./Contacts.module.scss"
import type { TestFunctionality } from "./TestFunctionality"
import {
  RemoveDialog,
  usePhoneNumberModalContext,
} from "src/components"
import {
  useOverlayLoader,
  usePolyglot,
} from "src/contexts"
import { useUpdateAlert } from "src/hooks"
import {
  RemovePhoneSuccessToast,
  GenericErrorToast,
} from "src/toasts"
import { formatPhoneNumber } from "src/utils"

const polyglotPrefix = "pages.alerts.contacts."

interface Props {
  phone: Phone;
  sugarmateAlert: Alert;
  testFunctionality?: TestFunctionality;
}

export function ContactCard(props: Props) {
  const {
    phone,
    sugarmateAlert,
    testFunctionality,
  } = props

  const [
    isRemoveOpen,
    setIsRemoveOpen,
  ] = useState(false)

  const polyglot = usePolyglot()
  const { showToast } = useToasterContext()
  const { setIsOverlayLoaderVisible } = useOverlayLoader()
  const { openPhoneNumberModal } = usePhoneNumberModalContext()
  const updateAlert = useUpdateAlert()

  const editPhoneNumber = useCallback(
    () => {
      openPhoneNumberModal(phone)
    },
    [
      phone,
      openPhoneNumberModal,
    ],
  )

  const onTestClick = useCallback(
    () => {
      if (testFunctionality) {
        testFunctionality.onClick(phone)
      }
    },
    [
      testFunctionality,
      phone,
    ],
  )

  // removes this phone number from this alert
  const removePhoneNumber = useCallback(
    () => {
      void (async () => {
        setIsOverlayLoaderVisible(true)

        try {
          if (!updateAlert) {
            throw new Error("cannot remove phone")
          }

          await updateAlert({
            id: sugarmateAlert.id,
            phone_ids: sugarmateAlert.phone_ids.filter((id) => id !== phone.id),
          })

          showToast(
            "remove-phone-success-toast",
            RemovePhoneSuccessToast,
          )
        } catch {
          showToast(
            "remove-phone-error-toast",
            GenericErrorToast,
          )
        }

        setIsOverlayLoaderVisible(false)
      })()
    },
    [
      updateAlert,
      phone,
      sugarmateAlert,
      showToast,
      setIsOverlayLoaderVisible,
    ],
  )

  const openRemoveDialog = useCallback(
    () => {
      setIsRemoveOpen(true)
    },
    [setIsRemoveOpen],
  )

  const phoneNumber = useMemo(
    () => formatPhoneNumber(phone.phone_number),
    [phone],
  )

  return (
    <LargeMenuModalItem className={styles.contactCard}>
      <LargeMenuModalItemButton>
        <LargeMenuModalItemIcon>
          <PhoneIcon />
        </LargeMenuModalItemIcon>
        <LargeMenuModalItemText>
          {phone.nickname && (
            <LargeMenuModalItemLabel>
              {phone.nickname}
            </LargeMenuModalItemLabel>
          )}
          <LargeMenuModalItemSubtext>
            {phoneNumber}
          </LargeMenuModalItemSubtext>
        </LargeMenuModalItemText>
      </LargeMenuModalItemButton>
      <LargeMenuModalItemIconButtons>
        <Popover placement="top-end">
          <PopoverTrigger asChild>
            <IconButton stylePreset="withoutBackground">
              <EllipsisIcon />
            </IconButton>
          </PopoverTrigger>
          <PopoverMenu
            aria-label={polyglot.t(`${polyglotPrefix}contact_aria_label`)}
          >
            {testFunctionality && phone.confirmed && (
              <>
                <PopoverMenuItem
                  onClick={onTestClick}
                  autoDismiss
                >
                  {testFunctionality.children}
                </PopoverMenuItem>
                <PopoverMenuItemSeparator />
              </>
            )}
            <PopoverMenuItem
              onClick={editPhoneNumber}
              autoDismiss
            >
              <PopoverMenuItemIcon>
                <EditIcon />
              </PopoverMenuItemIcon>
              {polyglot.t(`${polyglotPrefix}edit_cta`)}
            </PopoverMenuItem>
            <PopoverMenuItemSeparator />
            <PopoverMenuItem
              onClick={openRemoveDialog}
              autoDismiss
              className={styles.deleteButton}
            >
              <PopoverMenuItemIcon>
                <TrashCanOutlineIcon />
              </PopoverMenuItemIcon>
              {polyglot.t(`${polyglotPrefix}delete_cta`)}
            </PopoverMenuItem>
          </PopoverMenu>
        </Popover>
      </LargeMenuModalItemIconButtons>
      <RemoveDialog
        open={isRemoveOpen}
        setOpen={setIsRemoveOpen}
        remove={removePhoneNumber}
        title={polyglot.t(`${polyglotPrefix}remove_heading`)}
      >
        {polyglot.t(`${polyglotPrefix}remove_description`)}
      </RemoveDialog>
    </LargeMenuModalItem>
  )
}
